// SuccessPage — Paystack callback landing.
//
// Reads ?ref=<reference> from the URL and polls paystack_transactions
// until status='success' (or a 30s timeout). On success: 3-second
// celebration then navigate to /super-acca. On timeout: graceful
// "still processing" message.
//
// Doctrine: never fabricate. Until the webhook flips status='success',
// we tell the user honestly that we're waiting for confirmation.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getBrowserClient } from "../../supabase/browser.ts";

type Phase = "polling" | "success" | "timeout" | "error" | "missing_ref";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 30_000;
const REDIRECT_DELAY_MS = 3000;

export function SuccessPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const supabase = useMemo(() => getBrowserClient(), []);
  const reference = params.get("ref");
  const [phase, setPhase] = useState<Phase>(reference ? "polling" : "missing_ref");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!reference) return;
    let cancelled = false;
    const startedAt = Date.now();

    async function poll(): Promise<void> {
      while (!cancelled) {
        const elapsed = Date.now() - startedAt;
        if (elapsed > POLL_TIMEOUT_MS) {
          if (!cancelled) setPhase("timeout");
          return;
        }
        try {
          const res = await (supabase.from as unknown as (t: string) => {
            select: (cols: string) => {
              eq: (a: string, b: unknown) => {
                maybeSingle: () => Promise<{
                  data: { status: string } | null;
                  error: { message: string } | null;
                }>;
              };
            };
          })("paystack_transactions")
            .select("status")
            .eq("paystack_reference", reference)
            .maybeSingle();

          if (cancelled) return;
          if (res.error) {
            setErrorMsg(res.error.message);
          } else if (res.data?.status === "success") {
            setPhase("success");
            return;
          } else if (res.data?.status === "failed" || res.data?.status === "abandoned") {
            setPhase("error");
            setErrorMsg(`Payment ${res.data.status}.`);
            return;
          }
        } catch (err) {
          if (cancelled) return;
          setErrorMsg(err instanceof Error ? err.message : "Polling failed");
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
    }

    void poll();
    return () => {
      cancelled = true;
    };
  }, [reference, supabase]);

  useEffect(() => {
    if (phase !== "success") return;
    const t = setTimeout(() => {
      navigate("/super-acca");
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase, navigate]);

  return (
    <div
      style={{
        minHeight: "var(--vh-full)",
        background: "var(--bg, #0a0a0c)",
        color: "var(--text, #fff)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: "100%",
          background: "var(--surface, #0e0e10)",
          border: "1px solid var(--border, #2a2a30)",
          borderRadius: 12,
          padding: "36px 32px",
          textAlign: "center",
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            color: "var(--accent, #b07cff)",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          STRIKZA · checkout
        </div>

        {phase === "missing_ref" ? (
          <>
            <h1 style={{ fontSize: 22, margin: "0 0 12px" }}>No reference found</h1>
            <p style={{ fontSize: 14, color: "var(--text-muted, #c8cad0)", margin: "0 0 22px" }}>
              We couldn't find a payment reference in this URL. If you just paid, please refresh
              from your email confirmation link.
            </p>
            <Link
              to="/pricing"
              style={{ color: "var(--accent, #b07cff)", textDecoration: "none", fontSize: 13 }}
            >
              ← Back to pricing
            </Link>
          </>
        ) : phase === "polling" ? (
          <>
            <h1 style={{ fontSize: 22, margin: "0 0 12px" }}>Confirming your payment…</h1>
            <p style={{ fontSize: 14, color: "var(--text-muted, #c8cad0)", margin: "0 0 22px" }}>
              We're waiting for Paystack to confirm. This usually takes a few seconds.
            </p>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-tertiary, #888)" }}>
              Reference: {reference}
            </div>
          </>
        ) : phase === "success" ? (
          <>
            <h1 style={{ fontSize: 26, margin: "0 0 12px" }}>Welcome to STRIKZA Pro</h1>
            <p style={{ fontSize: 14, color: "var(--text-muted, #c8cad0)", margin: "0 0 22px" }}>
              Your subscription is active. Taking you to the Super Acca…
            </p>
          </>
        ) : phase === "timeout" ? (
          <>
            <h1 style={{ fontSize: 22, margin: "0 0 12px" }}>Still processing</h1>
            <p style={{ fontSize: 14, color: "var(--text-muted, #c8cad0)", margin: "0 0 22px" }}>
              Payment is processing — please refresh in a minute. If your card was charged but
              your subscription doesn't activate, contact support with reference{" "}
              <span className="mono">{reference}</span>.
            </p>
            <Link
              to="/pricing"
              style={{ color: "var(--accent, #b07cff)", textDecoration: "none", fontSize: 13 }}
            >
              Back to pricing
            </Link>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 22, margin: "0 0 12px" }}>Payment didn't complete</h1>
            <p style={{ fontSize: 14, color: "var(--text-muted, #c8cad0)", margin: "0 0 22px" }}>
              {errorMsg ?? "We couldn't confirm this payment."} You haven't been charged for an
              active subscription.
            </p>
            <Link
              to="/pricing"
              style={{ color: "var(--accent, #b07cff)", textDecoration: "none", fontSize: 13 }}
            >
              Try again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
